import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Typography } from '@components/Typography';
import { InternalLink } from '@components/Link';
import { billingService } from '@services/billing';
import { BILLING_TIME_MAP, DIRECTORIES_ADDON_STATUS } from '@constants/billing';
import { RoutePath } from '@constants/routes';
import { formatCurrency, getCurrentSubscription, getPricingPlansByCurrency } from '@helpers/billing';
import { useTranslation } from '@hooks';

const Container = styled('div')({
    padding: 16,
    borderRadius: 8,
    maxWidth: 698,
    border: '1px solid rgba(0, 0, 0, 0.12)',
    marginBottom: '24px',
});

const Row = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    paddingBottom: 10,
});

const Hint = styled(Typography)({
    display: 'block',
    color: 'var(--text-tertiary)',
    paddingTop: 10,
    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
});

const DirectoriesAddOn = ({ account }) => {
    const { t } = useTranslation();
    const [addOns, setAddOns] = useState(null);

    useEffect(() => {
        billingService
            .getDirectoriesAddOns()
            .then(setAddOns)
            .catch(() => setAddOns(null));
    }, []);

    if (!addOns?.allowed || addOns.includedInPlan) {
        return null;
    }

    const enrollments = (addOns.enrollments ?? []).filter(item => item.status === DIRECTORIES_ADDON_STATUS.ACTIVE);

    const subscriptions = account?.subscriptions ?? [];
    const groups = Object.values(
        enrollments.reduce((acc, enrollment) => {
            const subscription =
                subscriptions.find(item => item.id === enrollment.billingSubscriptionId) ??
                getCurrentSubscription(account);
            const isAnnual = subscription?.productType === BILLING_TIME_MAP.ANNUAL;
            const key = `${isAnnual}:${subscription?.currency ?? ''}`;
            acc[key] = acc[key] ?? { key, isAnnual, currency: subscription?.currency, items: [] };
            acc[key].items.push(enrollment);
            return acc;
        }, {}),
    );

    return (
        <>
            <Typography variant="h6" color="textPrimary" sx={{ marginBottom: '16px' }}>
                {t('SettingsCompanies.localDirectories')}
            </Typography>
            <Container>
                {groups.length ? (
                    groups.map(group => {
                        const pricing = getPricingPlansByCurrency(group.currency, account?.locale)[
                            group.isAnnual ? BILLING_TIME_MAP.ANNUAL : BILLING_TIME_MAP.MONTHLY
                        ];
                        const total = pricing.directories * group.items.length;
                        const period = group.isAnnual ? t('SettingsGeneral.year') : t('SettingsGeneral.month');

                        const names = group.items.map(item => item.locationName).filter(Boolean);

                        return (
                            <Row key={group.key}>
                                <div>
                                    <Typography
                                        variant="caption"
                                        sx={{ display: 'block', color: 'var(--text-tertiary)' }}
                                    >
                                        {t('SettingsGeneral.locationsEnrolled', { count: group.items.length })}
                                    </Typography>
                                    <Typography variant="caption">{names.join(', ')}</Typography>
                                </div>
                                <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap' }}>
                                    {`${formatCurrency(pricing.currency, total)} / ${period}`}
                                </Typography>
                            </Row>
                        );
                    })
                ) : (
                    <Row>
                        <Typography variant="caption" sx={{ color: 'var(--text-tertiary)' }}>
                            {t('SettingsGeneral.directoriesNoneEnrolled')}
                        </Typography>
                    </Row>
                )}
                <Hint variant="caption">
                    {t('SettingsGeneral.directoriesManagedPerLocation')}{' '}
                    <InternalLink to={RoutePath.Locations} variant="caption" underline="hover">
                        {t('SettingsGeneral.openLocations')}
                    </InternalLink>
                </Hint>
            </Container>
        </>
    );
};

export default DirectoriesAddOn;
